import { FormControl, InputAdornment, InputLabel, InputLabelProps, SelectProps, TextField } from '@material-ui/core';
import { Autocomplete, createFilterOptions } from '@material-ui/lab';
import { useCallback, useMemo } from 'react';
import { FieldValues } from 'react-hook-form';

import { Font } from '@/components/shared/components/Font';
import { useCustomFieldStyles } from '@/components/shared/components/NewContextForms/hooks/useCustomFieldStyles';
import { CustomFormLabelWithAdornment } from '@/components/shared/components/NewContextForms/shared/CustomFormLabelWithAdornment';
import { CustomTextFieldExtraProps } from '@/components/shared/components/NewContextForms/shared/CustomTextField';
import { CustomTextFieldLabel } from '@/components/shared/components/NewContextForms/shared/CustomTextFieldLabel';
import { FieldVariant } from '@/components/shared/components/NewContextForms/types/FieldVariant';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { SelectOption } from '@/types/selectOption';
import { translateZodNativeErrors } from '@/utils/zodUtils';

export interface CustomSelectFieldExtraProps {
  selectOptions: SelectOption[];
}

export interface CustomSelectFieldProps<TFieldValues extends FieldValues>
  extends Omit<SelectProps, 'name' | 'error' | 'style' | 'label'>,
    CustomTextFieldExtraProps<TFieldValues>,
    CustomSelectFieldExtraProps {
  InputLabelProps?: InputLabelProps;
}

export function CustomAutocompleteSelectField<TFieldValues extends FieldValues>({
  field,
  fieldVariant = FieldVariant.dataRow,
  error,
  tooltipTitle,
  fullWidth = true,
  valueUnit,
  labelIcon,
  label,
  strongTitle,
  labelDynamicDataTexts,
  labelIconPosition,
  highlightLabel,
  selectOptions,
  placeholder = 'SELECT',
  InputLabelProps,
  ...props
}: CustomSelectFieldProps<TFieldValues>) {
  const { getTextIdTranslation } = useTranslation();
  const { getMUISelectClasses, getMUIAutocompleteFormClasses } = useCustomFieldStyles({
    disabled: props.disabled,
    field: !!field,
    fieldVariant: fieldVariant,
    highlightLabel,
  });

  const fieldVariantIsForm = fieldVariant === FieldVariant.form;

  const selectClasses = getMUISelectClasses();
  const autocompleteClasses = getMUIAutocompleteFormClasses();

  const currentValue = useMemo(
    () => (field?.value === false ? false : props.value === false ? false : field?.value || props.value || ''),
    [field?.value, props.value],
  );

  const selectedOption = useMemo(
    () => selectOptions.find((option) => option.value === currentValue) || null,
    [selectOptions, currentValue],
  );

  const getOptionLabel = useCallback((option: SelectOption | string | null): string => {
    if (!option) return '';
    if (typeof option === 'string') return option;
    if (typeof option !== 'object' || !('value' in option)) {
      return '';
    }
    if (typeof option.value === 'string') {
      return getTextIdTranslation(option.value) || String(option.display);
    }
    return String(option.display);
  }, []);

  const filterOptions = useMemo(
    () =>
      createFilterOptions<SelectOption>({
        stringify: (option) => {
          if (!option || typeof option !== 'object' || !('value' in option)) {
            return '';
          }

          const valueStr = String(option.value);
          const displayStr = typeof option.display === 'string' ? option.display : String(option.display);

          return `${displayStr} ${valueStr}`;
        },
        trim: true,
        ignoreCase: true,
      }),
    [],
  );

  return (
    <>
      <FormControl fullWidth classes={selectClasses}>
        {fieldVariantIsForm && label && (
          <InputLabel shrink={fieldVariant === FieldVariant.form} {...InputLabelProps}>
            <CustomTextFieldLabel
              label={label}
              strongTitle={strongTitle}
              labelDynamicDataTexts={labelDynamicDataTexts}
              labelIcon={labelIcon}
              labelIconPosition={labelIconPosition}
              tooltipTitle={tooltipTitle}
              highlightLabel={highlightLabel}
            />
          </InputLabel>
        )}

        <Autocomplete
          filterOptions={filterOptions}
          classes={autocompleteClasses}
          options={selectOptions}
          value={selectedOption}
          onChange={(_, newValue, reason) => {
            if (!field) return;
            if (reason === 'clear') {
              field.onChange(null);
              return;
            }
            field.onChange(newValue?.value);
          }}
          getOptionLabel={getOptionLabel}
          getOptionSelected={(option, value) => {
            if (!option || !value) return option === value;
            if (typeof option === 'object' && typeof value === 'object' && 'value' in option && 'value' in value) {
              return option.value === value.value;
            }
            return false;
          }}
          renderOption={(option) => option.display}
          disabled={props.disabled}
          clearOnBlur={false}
          renderInput={(params) => {
            const inputValue = (params.inputProps as { value?: unknown }).value;
            const labelValue = selectedOption ? getOptionLabel(selectedOption) : '';
            const displayValue = typeof inputValue === 'string' ? inputValue : labelValue;
            return (
              <TextField
                {...params}
                fullWidth={fullWidth}
                placeholder={fieldVariantIsForm ? getTextIdTranslation(placeholder) || placeholder : undefined}
                error={!!error}
                helperText={translateZodNativeErrors(error?.message, getTextIdTranslation)}
                inputProps={{
                  ...params.inputProps,
                  value: displayValue,
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {valueUnit && props.value ? (
                        <InputAdornment position="end" style={{ paddingLeft: '4px' }}>
                          <Font variant="subtitle2" color={'textSecondary'}>
                            <strong>{valueUnit}</strong>
                          </Font>
                        </InputAdornment>
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                  startAdornment:
                    !fieldVariantIsForm && label ? (
                      <CustomFormLabelWithAdornment
                        tooltipTitle={tooltipTitle}
                        labelIcon={labelIcon}
                        label={label}
                        labelIconPosition={labelIconPosition}
                        labelDynamicDataTexts={labelDynamicDataTexts}
                      />
                    ) : (
                      params.InputProps.startAdornment
                    ),
                }}
                inputRef={field?.ref}
              />
            );
          }}
        />
      </FormControl>
    </>
  );
}
